import { z } from 'zod';
/**
 * Web Push contract (SPEC.md §5.6). Defined here because three places must agree:
 * the server that signs and sends, the service worker that renders the notification,
 * and any client code that reasons about it.
 */
/**
 * What the alert is about — drives icon, urgency and grouping in the service worker.
 *
 * `TicketResolved` and `TicketRejected` are the two halves of verification: work handed
 * up for checking, and work handed back. They are separate categories rather than reuses
 * of `TicketAssigned` because the recipient sets differ — one goes to the verifiers, the
 * other to the person whose fix was refused — and the notification feed is filtered and
 * read by category.
 */
export const PushCategory = {
    /**
     * A new job offered to a department (D45). Filed under `TicketAssigned` until then,
     * which made "new job in your department" and "this one is yours" the same category —
     * invisible while the drawer rendered only title and body, and fatal to any future
     * filtering or muting, which is exactly what a category exists to serve.
     */
    TicketCreated: 'TICKET_CREATED',
    TicketAssigned: 'TICKET_ASSIGNED',
    /** A job taken off you and given to someone else (D38) — the other half of assignment. */
    TicketReassigned: 'TICKET_REASSIGNED',
    TicketEscalated: 'TICKET_ESCALATED',
    TicketResolved: 'TICKET_RESOLVED',
    TicketRejected: 'TICKET_REJECTED',
    /**
     * Somebody else paused your job, and the reason why (D1 of the hold brief). Hold and
     * resume were the only transitions that announced nothing at all, which was
     * acceptable while only the assignee could fire them — they already knew. Once a
     * manager can pause someone else's job, silence means a technician walking to a plant
     * room to work on a job that was put on hold twenty minutes ago.
     */
    TicketHeld: 'TICKET_HELD',
    /** The other half: somebody else resumed your job, so it is live again. */
    TicketResumed: 'TICKET_RESUMED',
    ShiftUpdated: 'SHIFT_UPDATED',
    SwapRequested: 'SWAP_REQUESTED',
    /**
     * A direct message (D45). The one place in the app where a person is directly
     * addressed, and until this existed it produced no notification of any kind — a
     * recipient not sitting on /messages learned nothing.
     */
    DirectMessage: 'DIRECT_MESSAGE',
    /**
     * "Send me a test notification" from the settings switch. Its own category rather
     * than borrowed from a real one: a feed row filed as an assignment that never
     * happened would be a lie, and any future mute keyed on category (DEBT-19) must be
     * able to tell a rehearsal from work.
     */
    Test: 'TEST',
};
export const pushCategorySchema = z.enum([
    PushCategory.TicketCreated,
    PushCategory.TicketAssigned,
    PushCategory.TicketReassigned,
    PushCategory.TicketEscalated,
    PushCategory.TicketResolved,
    PushCategory.TicketRejected,
    PushCategory.TicketHeld,
    PushCategory.TicketResumed,
    PushCategory.ShiftUpdated,
    PushCategory.SwapRequested,
    PushCategory.DirectMessage,
    PushCategory.Test,
]);
/**
 * Payload delivered to the service worker.
 *
 * Title and body are pre-rendered strings, not i18n keys: the service worker has no
 * i18n runtime, and the server already knows the recipient's locale.
 */
export const pushPayloadSchema = z.object({
    title: z.string().min(1),
    body: z.string(),
    category: pushCategorySchema,
    /** Path to open on tap, e.g. "/tickets/abc123". */
    url: z.string().optional(),
    /**
     * Collapse key. A second alert about the same ticket replaces the first rather than
     * stacking — a technician should not wade through six notifications for one fault.
     */
    tag: z.string().optional(),
});
/** Body of POST /api/push/subscribe — mirrors the browser's PushSubscription JSON. */
export const pushSubscribeBodySchema = z.object({
    endpoint: z.string().url(),
    keys: z.object({
        p256dh: z.string().min(1),
        auth: z.string().min(1),
    }),
    deviceName: z.string().max(120).optional(),
});
export const pushUnsubscribeBodySchema = z.object({
    endpoint: z.string().url(),
});
//# sourceMappingURL=push-payload.js.map